/** Auth state — сессия в httpOnly-куках; здесь только user + CSRF-токен. */

import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { AdminUser } from "@/lib/types";

// Ключ старого стора, где access/refresh лежали в localStorage (XSS-риск).
// Читается один раз, чтобы обменять старую сессию на httpOnly-куки.
const LEGACY_STORAGE_KEY = "ri-admin-auth";

interface AuthState {
  csrfToken: string | null;
  user: AdminUser | null;
  setSession: (csrfToken: string) => void;
  setUser: (user: AdminUser) => void;
  refresh: () => Promise<void>;
  logout: () => void;
}

function legacyRefreshToken(): string | null {
  try {
    const raw = localStorage.getItem(LEGACY_STORAGE_KEY);
    return raw ? (JSON.parse(raw)?.state?.refreshToken ?? null) : null;
  } catch {
    return null;
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      csrfToken: null,
      user: null,

      setSession: (csrfToken) => set({ csrfToken }),

      setUser: (user) => set({ user }),

      refresh: async () => {
        const legacy = legacyRefreshToken();
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/auth/refresh`,
          {
            method: "POST",
            credentials: "include",
            ...(legacy
              ? {
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ refresh_token: legacy }),
                }
              : {}),
          },
        );

        if (!response.ok) {
          throw new Error("Refresh failed");
        }

        const data = await response.json();
        set({ csrfToken: data.csrf_token });
        localStorage.removeItem(LEGACY_STORAGE_KEY);
      },

      logout: () => {
        set({ csrfToken: null, user: null });
        localStorage.removeItem(LEGACY_STORAGE_KEY);
      },
    }),
    {
      name: "ri-admin-auth-v2",
      // user не персистим — перезагружается через GET /me. csrf-токен не
      // защищает от XSS (XSS и так обходит CSRF), поэтому localStorage ок —
      // он нужен, чтобы переживать перезагрузку страницы.
      partialize: (state) => ({ csrfToken: state.csrfToken }),
    },
  ),
);
